import {
  Plus,
  FileText,
  Award,
  CheckCircle2,
  Clock,
  BarChart3,
  X,
  SlidersHorizontal,
  ChevronRight,
} from 'lucide-react';
import { MobilePageWrapper } from '../../components/layout/MobilePageWrapper';
import type { Avaliacao } from '../../api/hooks/useAvaliacoes';
import {
  BIMESTRES,
  TIPO_LABELS,
  STATUS_LABELS,
  metaAvaliacao,
} from './avaliacoes-professor-shared';
import { AvaliacaoDetalhePanel } from './AvaliacaoDetalhePanel';
import { AvaliacoesFormBlock } from './AvaliacoesFormBlock';
import { AvaliacoesNotasSheet } from './AvaliacoesNotasSheet';
import type { AvaliacoesProfessorPageContext } from './useAvaliacoesProfessorPage';

interface Props {
  ctx: AvaliacoesProfessorPageContext;
}

export function AvaliacoesMobileView({ ctx }: Props) {
  const {
    stats,
    showForm,
    setShowForm,
    filtrosAbertos,
    setFiltrosAbertos,
    filtrosAtivos,
    turmaId,
    setTurmaId,
    materiaId,
    setMateriaId,
    bimestre,
    setBimestre,
    minhasTurmas,
    minhasMaterias,
    isLoading,
    avaliacoes,
    detalheId,
    setDetalheId,
    avaliacaoDetalhe,
    historicoNotasQuery,
    abrirLancarNotas,
    handleMudarStatus,
    mudarStatus,
  } = ctx;

  const renderCard = (av: Avaliacao) => {
    const tipoInfo = TIPO_LABELS[av.tipo];
    const statusInfo = STATUS_LABELS[av.status];
    const notasCount = av._count?.notas ?? 0;
    const abrirDetalhe = () => setDetalheId(av.id);

    return (
      <article
        key={av.id}
        className="aval-item aval-item--tap"
        onClick={abrirDetalhe}
        onKeyDown={(e) => e.key === 'Enter' && abrirDetalhe()}
        role="button"
        tabIndex={0}
        aria-label={`${av.titulo}, ${statusInfo.label}, ${notasCount} notas`}
      >
        <div
          className="aval-item-icon"
          style={{ background: `${tipoInfo.color}18`, color: tipoInfo.color }}
        >
          {tipoInfo.icon}
        </div>
        <div className="aval-item-body">
          <div className="aval-item-title-row">
            <span className="aval-item-title">{av.titulo}</span>
            <span
              className="aval-item-status"
              style={{ background: statusInfo.bg, color: statusInfo.color }}
            >
              {statusInfo.label}
            </span>
          </div>
          <p className="aval-item-meta">{metaAvaliacao(av)}</p>
        </div>
        <div className="aval-item-aside">
          <span className="aval-item-notas-count">{notasCount} notas</span>
          <ChevronRight size={16} className="aval-item-chevron" aria-hidden />
        </div>
      </article>
    );
  };

  return (
    <MobilePageWrapper
      title="Avaliações"
      icon={Award}
      iconColor="#c4b5fd"
      badge={stats.pendentes > 0 ? `${stats.pendentes} pend.` : undefined}
      badgeColor="#f59e0b"
    >
      <div className="admin-page aval-page-mobile aval-page-root">
        <div className="aval-mobile-only">
          <div className="aval-toolbar">
            <button type="button" className="aval-btn-nova-compact" onClick={() => setShowForm(!showForm)}>
              <Plus size={16} aria-hidden />
              Nova avaliação
            </button>
            <button
              type="button"
              className={`aval-filter-toggle ${filtrosAbertos || filtrosAtivos ? 'active' : ''}`}
              onClick={() => setFiltrosAbertos(!filtrosAbertos)}
              aria-label="Filtros"
              aria-expanded={filtrosAbertos}
            >
              <SlidersHorizontal size={16} />
            </button>
          </div>

          <div className="aval-kpi-row-compact">
            <div className="aval-kpi-chip">
              <BarChart3 size={14} style={{ color: '#7c3aed' }} aria-hidden />
              <span className="aval-kpi-chip-value">{stats.total}</span>
              <span className="aval-kpi-chip-label">Total</span>
            </div>
            <div className="aval-kpi-chip">
              <CheckCircle2 size={14} style={{ color: '#22c55e' }} aria-hidden />
              <span className="aval-kpi-chip-value">{stats.concluidas}</span>
              <span className="aval-kpi-chip-label">Concluídas</span>
            </div>
            <div className="aval-kpi-chip">
              <Clock size={14} style={{ color: '#f59e0b' }} aria-hidden />
              <span className="aval-kpi-chip-value">{stats.pendentes}</span>
              <span className="aval-kpi-chip-label">Pendentes</span>
            </div>
          </div>

          {filtrosAbertos && (
            <div className="aval-filtros-panel">
              <div className="aval-filtro-field">
                <label className="aval-filtro-label" htmlFor="aval-filtro-turma-m">
                  Turma
                </label>
                <select
                  id="aval-filtro-turma-m"
                  className="aval-filtro-select"
                  value={turmaId}
                  onChange={(e) => setTurmaId(e.target.value)}
                >
                  <option value="">Todas as turmas</option>
                  {minhasTurmas.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="aval-filtro-field">
                <label className="aval-filtro-label" htmlFor="aval-filtro-materia-m">
                  Materia
                </label>
                <select
                  id="aval-filtro-materia-m"
                  className="aval-filtro-select"
                  value={materiaId}
                  onChange={(e) => setMateriaId(e.target.value)}
                >
                  <option value="">Todas as materias</option>
                  {minhasMaterias.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="aval-filtro-field">
                <label className="aval-filtro-label" htmlFor="aval-filtro-bim-m">
                  Bimestre
                </label>
                <select
                  id="aval-filtro-bim-m"
                  className="aval-filtro-select"
                  value={bimestre}
                  onChange={(e) => setBimestre(Number(e.target.value))}
                >
                  <option value={0}>Todos os bimestres</option>
                  {BIMESTRES.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {showForm && (
            <div className="aval-form-sheet">
              <h3>Nova avaliação</h3>
              <AvaliacoesFormBlock {...ctx} />
            </div>
          )}

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
              Carregando...
            </div>
          ) : avaliacoes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
              <FileText size={36} style={{ opacity: 0.25, marginBottom: 8 }} />
              <p style={{ fontSize: '0.82rem', margin: 0 }}>Nenhuma avaliação encontrada</p>
            </div>
          ) : (
            <div className="aval-list-compact">{avaliacoes.map(renderCard)}</div>
          )}
        </div>

        {avaliacaoDetalhe && (
          <div className="comunicado-sheet-overlay" onClick={() => setDetalheId(null)} role="presentation">
            <div
              className="comunicado-sheet-panel aval-detalhe-sheet"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-labelledby="aval-detalhe-title"
            >
              <div className="comunicado-sheet-handle" aria-hidden />
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="comunicado-sheet-close"
                  onClick={() => setDetalheId(null)}
                  aria-label="Fechar"
                  style={{ position: 'absolute', right: 8, top: 8, zIndex: 2 }}
                >
                  <X size={18} />
                </button>
                <AvaliacaoDetalhePanel
                  av={avaliacaoDetalhe}
                  historicoNotasQuery={historicoNotasQuery}
                  onLancarNotas={abrirLancarNotas}
                  onMudarStatus={handleMudarStatus}
                  mudarStatusPending={mudarStatus.isPending}
                  variant="sheet"
                />
                <button
                  type="button"
                  className="aval-detalhe-btn-fechar"
                  onClick={() => setDetalheId(null)}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        <AvaliacoesNotasSheet {...ctx} />
      </div>
    </MobilePageWrapper>
  );
}
